import type {
  VerifyCodeResponse,
  ProjectCreateResponse,
  ProjectSendResponse,
  ProjectDetail,
  ProjectSummary,
  RunPipelineResponse,
  ArtifactsResponse,
  FeedbackStartResponse,
  FeedbackSendResponse,
  FeedbackResponse,
  AdminProjectsResponse,
  ReviewStartResponse,
  ReviewSendResponse,
  ReviewResponse,
  PartialAssessmentResponse,
  PurchaseAssessmentResponse,
  PurchaseBusinessPlanResponse,
  FullAssessmentResponse,
  PricingResponse,
  PipelineReviewStateResponse,
  PipelineReviewGenerateResponse,
  PipelineReviewMessageResponse,
  PipelineReviewApproveResponse,
} from "./types";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8000";

export class ApiError extends Error {
  status: number;
  retry: boolean;
  detail: unknown;
  constructor(message: string, status: number, retry = false, detail?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.retry = retry;
    this.detail = detail;
  }
}

let inviteCode: string | null = null;
let sessionToken: string | null = null;

export function setInviteCode(code: string) {
  inviteCode = code;
  localStorage.setItem("invite_code", code);
}

export function getInviteCode(): string | null {
  if (!inviteCode) {
    inviteCode = localStorage.getItem("invite_code");
  }
  return inviteCode;
}

export function clearInviteCode() {
  inviteCode = null;
  localStorage.removeItem("invite_code");
}

export function setSessionToken(token: string) {
  sessionToken = token;
  localStorage.setItem("architect-session", token);
}

export function getSessionToken(): string | null {
  if (!sessionToken) {
    sessionToken = localStorage.getItem("architect-session");
  }
  return sessionToken;
}

export function clearSessionToken() {
  sessionToken = null;
  localStorage.removeItem("architect-session");
}

export function setStoredEmail(email: string) {
  localStorage.setItem("architect-email", email);
}

export function getStoredEmail(): string | null {
  return localStorage.getItem("architect-email");
}

/** Returns true if the user has any form of authentication. */
export function isAuthenticated(): boolean {
  return !!(getInviteCode() || getSessionToken());
}

/** Clear all auth state. */
export function clearAuth() {
  clearInviteCode();
  clearSessionToken();
  localStorage.removeItem("architect-email");
}

function addAuthHeaders(headers: Record<string, string>): void {
  const token = getSessionToken();
  const code = getInviteCode();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  } else if (code) {
    headers["X-Invite-Code"] = code;
  }
}

async function apiFetch<T>(path: string, options: RequestInit = {}, _retried = false): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string> | undefined),
  };
  addAuthHeaders(headers);

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  if (!res.ok) {
    // If session token is stale (deleted from DB), clear it and retry once without auth
    if (res.status === 401 && getSessionToken() && !_retried) {
      clearSessionToken();
      return apiFetch<T>(path, options, true);
    }
    const err = await res.json().catch(() => ({ detail: "Request failed" }));
    const detail = err.detail;
    const message = typeof detail === "string" ? detail : (detail?.message || `HTTP ${res.status}`);
    throw new ApiError(
      message,
      res.status,
      !!err.retry,
      detail,
    );
  }
  return res.json();
}

export const api = {
  healthCheck: (): Promise<boolean> =>
    fetch(`${API_BASE}/api/health`).then((r) => r.ok),

  verifyCode: (code: string, email: string = ""): Promise<VerifyCodeResponse> =>
    apiFetch("/api/auth/verify-code", {
      method: "POST",
      body: JSON.stringify({ code, email }),
    }),

  requestMagicLink: (email: string): Promise<{ sent: boolean; message: string }> =>
    apiFetch("/api/auth/magic-link", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  verifyMagicLink: (token: string): Promise<{
    valid: boolean;
    session_token: string;
    invite_code: string;
    email: string;
    is_admin: boolean;
    runs_remaining: number;
    pilot_mode: boolean;
    pilot_tier: number | null;
  }> => apiFetch(`/api/auth/verify-magic-link?token=${encodeURIComponent(token)}`),

  // --- Projects ---

  listProjects: (): Promise<{ projects: ProjectSummary[]; is_admin: boolean }> =>
    apiFetch("/api/projects"),

  listAdminProjects: (): Promise<AdminProjectsResponse> =>
    apiFetch("/api/admin/projects"),

  createProject: (goal: string, title?: string, language?: string, email?: string): Promise<ProjectCreateResponse> =>
    apiFetch("/api/projects", {
      method: "POST",
      body: JSON.stringify({ goal, title, language, email }),
    }),

  getProject: (projectId: string): Promise<ProjectDetail> =>
    apiFetch(`/api/projects/${projectId}`),

  sendMessage: (projectId: string, message: string): Promise<ProjectSendResponse> =>
    apiFetch(`/api/projects/${projectId}/send`, {
      method: "POST",
      body: JSON.stringify({ message }),
    }),

  runPipeline: (projectId: string): Promise<RunPipelineResponse> =>
    apiFetch(`/api/projects/${projectId}/pipeline`, {
      method: "POST",
    }),

  deleteProject: (projectId: string): Promise<{ deleted: boolean; runs_credited: number }> =>
    apiFetch(`/api/projects/${projectId}`, { method: "DELETE" }),

  archiveProject: (projectId: string): Promise<{ archived: boolean; project_id: string }> =>
    apiFetch(`/api/projects/${projectId}/archive`, { method: "POST" }),

  reactivateProject: (projectId: string): Promise<{ reactivated: boolean; project_id: string; auto_archived_project_id: string | null }> =>
    apiFetch(`/api/projects/${projectId}/reactivate`, { method: "POST" }),

  renameProject: (projectId: string, title: string): Promise<{ title: string }> =>
    apiFetch(`/api/projects/${projectId}/title`, {
      method: "PATCH",
      body: JSON.stringify({ title }),
    }),

  exportProject: async (projectId: string): Promise<void> => {
    const headers: Record<string, string> = {};
    addAuthHeaders(headers);
    const res = await fetch(`${API_BASE}/api/projects/${projectId}/export`, { headers });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: "Export failed" }));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const disposition = res.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : "project.zip";
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  exportAdminGroup: async (inviteCode: string): Promise<void> => {
    const headers: Record<string, string> = {};
    addAuthHeaders(headers);
    const res = await fetch(`${API_BASE}/api/admin/export/${encodeURIComponent(inviteCode)}`, { headers });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: "Export failed" }));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const disposition = res.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : `${inviteCode}.zip`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  recordMvpInterest: (projectId: string): Promise<{ recorded: boolean }> =>
    apiFetch(`/api/projects/${projectId}/mvp-interest`, { method: "POST" }),

  // --- Business Case Review ---

  generateBusinessCase: (projectId: string): Promise<RunPipelineResponse> =>
    apiFetch(`/api/projects/${projectId}/generate-business-case`, { method: "POST" }),

  startReview: (projectId: string): Promise<ReviewStartResponse> =>
    apiFetch(`/api/projects/${projectId}/review/start`, { method: "POST" }),

  sendReviewMessage: (projectId: string, message: string): Promise<ReviewSendResponse> =>
    apiFetch(`/api/projects/${projectId}/review/send`, {
      method: "POST",
      body: JSON.stringify({ message }),
    }),

  getReview: (projectId: string): Promise<ReviewResponse> =>
    apiFetch(`/api/projects/${projectId}/review`),

  approveBusinessCase: (projectId: string, mode: string = "auto"): Promise<RunPipelineResponse> =>
    apiFetch(`/api/projects/${projectId}/approve-business-case?mode=${mode}`, { method: "POST" }),

  // --- Quick Feedback ---

  submitQuickFeedback: (projectId: string, feedback: string): Promise<{ ok: boolean }> =>
    apiFetch(`/api/projects/${projectId}/quick-feedback`, {
      method: "POST",
      body: JSON.stringify({ feedback }),
    }),

  // --- Feedback ---

  startFeedback: (projectId: string): Promise<FeedbackStartResponse> =>
    apiFetch(`/api/projects/${projectId}/feedback/start`, { method: "POST" }),

  sendFeedbackMessage: (projectId: string, message: string): Promise<FeedbackSendResponse> =>
    apiFetch(`/api/projects/${projectId}/feedback/send`, {
      method: "POST",
      body: JSON.stringify({ message }),
    }),

  getFeedback: (projectId: string): Promise<FeedbackResponse> =>
    apiFetch(`/api/projects/${projectId}/feedback`),

  // --- Pipeline SSE ---

  pipelineStatus: (runId: string): EventSource => {
    return new EventSource(`${API_BASE}/api/pipeline/status/${runId}`);
  },

  // --- Artifacts ---

  listArtifacts: (runId: string): Promise<ArtifactsResponse> =>
    apiFetch(`/api/artifacts/${runId}`),

  getArtifactContent: async (runId: string, filename: string): Promise<string> => {
    const headers: Record<string, string> = {};
    addAuthHeaders(headers);
    const res = await fetch(`${API_BASE}/api/artifacts/${runId}/${filename}`, { headers });
    if (!res.ok) throw new Error("Failed to download artifact");
    return res.text();
  },

  // --- Telemetry ---

  logEvent: (projectId: string, eventType: string, eventData?: Record<string, unknown>): void => {
    apiFetch(`/api/projects/${projectId}/event`, {
      method: "POST",
      body: JSON.stringify({ event_type: eventType, event_data: eventData }),
    }).catch(() => {}); // fire-and-forget
  },

  // --- Partial / Full Assessment (Paywall Specs B/C/D) ---

  generatePartialAssessment: (projectId: string): Promise<PartialAssessmentResponse> =>
    apiFetch(`/api/projects/${projectId}/generate-partial-assessment`, { method: "POST" }),

  purchaseAssessment: (projectId: string): Promise<PurchaseAssessmentResponse> =>
    apiFetch(`/api/projects/${projectId}/purchase-assessment`, { method: "POST" }),

  purchaseBusinessPlan: (projectId: string): Promise<PurchaseBusinessPlanResponse> =>
    apiFetch(`/api/projects/${projectId}/purchase-business-plan`, { method: "POST" }),

  getFullAssessment: (projectId: string): Promise<FullAssessmentResponse> =>
    apiFetch(`/api/projects/${projectId}/full-assessment`),

  getPricing: (projectId: string): Promise<PricingResponse> =>
    apiFetch(`/api/projects/${projectId}/pricing`),

  regenerateAssessment: (projectId: string): Promise<{ status: string }> =>
    apiFetch(`/api/projects/${projectId}/regenerate-assessment`, { method: "POST" }),

  generateFullAssessment: (projectId: string): Promise<{ status: string; full_assessment?: string; phase?: string }> =>
    apiFetch(`/api/projects/${projectId}/generate-full-assessment`, { method: "POST" }),

  // --- Assessment downloads (Spec G) ---

  openAssessmentHtml: async (projectId: string): Promise<void> => {
    const headers: Record<string, string> = {};
    addAuthHeaders(headers);
    const res = await fetch(`${API_BASE}/api/projects/${projectId}/assessment.html`, { headers });
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  },

  openExecSummaryHtml: async (projectId: string): Promise<void> => {
    const headers: Record<string, string> = {};
    addAuthHeaders(headers);
    const res = await fetch(`${API_BASE}/api/projects/${projectId}/executive-summary.html`, { headers });
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  },

  downloadAssessmentMd: async (projectId: string): Promise<void> => {
    const headers: Record<string, string> = {};
    addAuthHeaders(headers);
    const res = await fetch(`${API_BASE}/api/projects/${projectId}/assessment.md`, { headers });
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "viability-assessment.md";
    a.click();
    URL.revokeObjectURL(url);
  },

  downloadAssessmentPdf: async (projectId: string): Promise<void> => {
    const headers: Record<string, string> = {};
    addAuthHeaders(headers);
    const res = await fetch(`${API_BASE}/api/projects/${projectId}/assessment.pdf`, { headers });
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const contentType = res.headers.get("Content-Type") || "";
    if (contentType.includes("application/pdf")) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "viability-assessment.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } else {
      // HTML fallback — open in new tab for print-to-PDF
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    }
  },

  downloadBriefPdf: async (projectId: string): Promise<void> => {
    const headers: Record<string, string> = {};
    addAuthHeaders(headers);
    const res = await fetch(`${API_BASE}/api/projects/${projectId}/brief-assessment.pdf`, { headers });
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const contentType = res.headers.get("Content-Type") || "";
    if (contentType.includes("application/pdf")) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "brief-assessment.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } else {
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    }
  },

  downloadExecSummaryPdf: async (projectId: string): Promise<void> => {
    const headers: Record<string, string> = {};
    addAuthHeaders(headers);
    const res = await fetch(`${API_BASE}/api/projects/${projectId}/executive-summary.pdf`, { headers });
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const contentType = res.headers.get("Content-Type") || "";
    if (contentType.includes("application/pdf")) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "executive-summary.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } else {
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    }
  },

  // --- Pipeline Review ($249 tier) ---

  getPipelineReviewState: (projectId: string): Promise<PipelineReviewStateResponse> =>
    apiFetch(`/api/projects/${projectId}/pipeline-review/state`),

  generatePipelineStage: (projectId: string, feedback?: string): Promise<PipelineReviewGenerateResponse> =>
    apiFetch(`/api/projects/${projectId}/pipeline-review/generate`, {
      method: "POST",
      body: JSON.stringify({ feedback: feedback || null }),
    }),

  sendPipelineReviewMessage: (projectId: string, message: string): Promise<PipelineReviewMessageResponse> =>
    apiFetch(`/api/projects/${projectId}/pipeline-review/message`, {
      method: "POST",
      body: JSON.stringify({ message }),
    }),

  approvePipelineStage: (projectId: string): Promise<PipelineReviewApproveResponse> =>
    apiFetch(`/api/projects/${projectId}/pipeline-review/approve`, {
      method: "POST",
    }),

  downloadPipelineStage: async (projectId: string, stage: string): Promise<void> => {
    const headers: Record<string, string> = {};
    addAuthHeaders(headers);
    const res = await fetch(
      `${API_BASE}/api/projects/${projectId}/pipeline-review/download/${stage}`,
      { headers },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: "Download failed" }));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const contentType = res.headers.get("Content-Type") || "";
    const isPdf = contentType.includes("application/pdf");
    if (isPdf) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${stage.replace("_", "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      // HTML fallback — open in new tab for print-to-PDF
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    }
  },
};
